// Painel de Monitoramento para Porteiros - DNA GO
// Tela fullscreen otimizada para TV/monitor na portaria
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import DnaGoDnAiWidget from './DnaGoDnAiWidget';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { 
  Volume2, VolumeX, Maximize, Minimize, Settings, 
  Clock, Users, Bell, Moon, Sun, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

// Configuração de níveis de ensino com sons e cores
const NIVEIS_CONFIG = {
  'infantil': {
    nome: 'Educação Infantil',
    cor: 'bg-pink-500',
    corBorda: 'border-pink-500',
    corTexto: 'text-pink-700',
    corFundo: 'bg-pink-50',
    frequencia: 800,
    duracao: 300,
    repeticoes: 1
  },
  'fundamental1': {
    nome: 'Fundamental I',
    cor: 'bg-blue-500',
    corBorda: 'border-blue-500',
    corTexto: 'text-blue-700',
    corFundo: 'bg-blue-50',
    frequencia: 600,
    duracao: 200,
    repeticoes: 2
  },
  'fundamental2': {
    nome: 'Fundamental II',
    cor: 'bg-green-500',
    corBorda: 'border-green-500',
    corTexto: 'text-green-700',
    corFundo: 'bg-green-50',
    frequencia: 500,
    duracao: 150,
    repeticoes: 3
  },
  'medio': {
    nome: 'Ensino Médio',
    cor: 'bg-purple-500',
    corBorda: 'border-purple-500',
    corTexto: 'text-purple-700',
    corFundo: 'bg-purple-50',
    frequencia: 400,
    duracao: 100,
    repeticoes: 4
  }
};

// Função para identificar nível de ensino pela turma
const identificarNivel = (turmaNome) => {
  if (!turmaNome) return 'fundamental1';
  const nome = turmaNome.toLowerCase();
  
  if (nome.includes('infantil') || nome.includes('maternal') || nome.includes('jardim') || nome.includes('pré')) {
    return 'infantil';
  }
  if (nome.includes('médio') || nome.includes('medio') || nome.includes('1ª série') || nome.includes('2ª série') || nome.includes('3ª série')) {
    return 'medio';
  }
  if (nome.includes('6º') || nome.includes('7º') || nome.includes('8º') || nome.includes('9º') || 
      nome.includes('6°') || nome.includes('7°') || nome.includes('8°') || nome.includes('9°')) {
    return 'fundamental2';
  }
  // Default para Fundamental I (1º ao 5º ano)
  return 'fundamental1';
};

// Componente de geração de som
const useSom = () => {
  const audioContextRef = useRef(null);
  
  const tocarSom = useCallback((nivel, volume = 0.5) => {
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      
      const ctx = audioContextRef.current;
      const config = NIVEIS_CONFIG[nivel] || NIVEIS_CONFIG.fundamental1;
      
      for (let i = 0; i < config.repeticoes; i++) {
        setTimeout(() => {
          const oscillator = ctx.createOscillator();
          const gainNode = ctx.createGain();
          
          oscillator.connect(gainNode);
          gainNode.connect(ctx.destination);
          
          oscillator.frequency.value = config.frequencia;
          oscillator.type = 'sine';
          
          gainNode.gain.setValueAtTime(volume, ctx.currentTime);
          gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + config.duracao / 1000);
          
          oscillator.start(ctx.currentTime);
          oscillator.stop(ctx.currentTime + config.duracao / 1000);
        }, i * (config.duracao + 100));
      }
    } catch (e) {
      console.error('Erro ao tocar som:', e);
    }
  }, []);
  
  return { tocarSom };
};

export default function DnaGoPainelPorteiro() {
  const [fila, setFila] = useState([]);
  const [chamados, setChamados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [volume, setVolume] = useState(50);
  const [somAtivo, setSomAtivo] = useState(true);
  const [modoNoturno, setModoNoturno] = useState(false);
  const [contadores, setContadores] = useState({});
  const [ultimosChamados, setUltimosChamados] = useState(new Set());
  
  const { tocarSom } = useSom();
  const refreshInterval = useRef(null);

  // Carregar dados
  const loadData = useCallback(async () => {
    try {
      const response = await api.get('/dnago/fila');
      const filaData = response.data || [];
      
      // Separar chamados e aguardando
      const chamadosAtual = filaData.filter(item => item.status === 'chamado');
      const aguardando = filaData.filter(item => item.status === 'aguardando');
      
      // Verificar novos chamados para tocar som
      if (somAtivo && !modoNoturno) {
        chamadosAtual.forEach(item => {
          if (!ultimosChamados.has(item.aluno_id)) {
            const nivel = identificarNivel(item.turma_nome);
            tocarSom(nivel, volume / 100);
          }
        });
      }
      
      // Atualizar set de últimos chamados
      setUltimosChamados(new Set(chamadosAtual.map(item => item.aluno_id)));
      
      setFila(aguardando);
      setChamados(chamadosAtual);
      
      // Calcular contadores por nível
      const counts = {};
      aguardando.forEach(item => {
        const nivel = identificarNivel(item.turma_nome);
        counts[nivel] = (counts[nivel] || 0) + 1;
      });
      setContadores(counts);
      
      setLoading(false);
    } catch (error) {
      console.error('Erro ao carregar fila:', error);
    }
  }, [somAtivo, modoNoturno, volume, tocarSom, ultimosChamados]);

  useEffect(() => {
    loadData();
    refreshInterval.current = setInterval(loadData, 2000); // Refresh mais rápido para portaria
    return () => clearInterval(refreshInterval.current);
  }, [loadData]);

  // Fullscreen
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Tempo formatado
  const formatarTempo = (dataStr) => {
    if (!dataStr) return '';
    const diff = Date.now() - new Date(dataStr).getTime();
    const minutos = Math.floor(diff / 60000);
    if (minutos < 1) return 'Agora';
    return `${minutos} min`;
  };

  // Hora atual
  const [horaAtual, setHoraAtual] = useState(new Date());
  useEffect(() => {
    const timer = setInterval(() => setHoraAtual(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className={`min-h-screen ${modoNoturno ? 'bg-gray-900' : 'bg-gradient-to-br from-gray-100 to-gray-200'} p-4 md:p-6`}>
      {/* Header */}
      <div className={`flex items-center justify-between mb-6 ${modoNoturno ? 'text-white' : 'text-gray-800'}`}>
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-xl ${modoNoturno ? 'bg-purple-600' : 'bg-purple-500'} flex items-center justify-center shadow-lg`}>
            <Bell className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold">DNA GO - Portaria</h1>
            <p className={`text-sm ${modoNoturno ? 'text-gray-400' : 'text-gray-500'}`}>
              Painel de Monitoramento de Saída
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          {/* Relógio */}
          <div className={`text-3xl md:text-4xl font-mono font-bold ${modoNoturno ? 'text-white' : 'text-gray-700'}`}>
            {horaAtual.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
          </div>
          
          {/* Controles */}
          <div className="flex items-center gap-2">
            <Button
              variant={modoNoturno ? "secondary" : "outline"}
              size="icon"
              onClick={() => setModoNoturno(!modoNoturno)}
              title={modoNoturno ? "Modo Dia" : "Modo Noite"}
            >
              {modoNoturno ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
            
            <Button
              variant={somAtivo ? "default" : "outline"}
              size="icon"
              onClick={() => setSomAtivo(!somAtivo)}
              className={somAtivo ? "bg-green-600 hover:bg-green-700" : ""}
              title={somAtivo ? "Som Ativo" : "Som Desativado"}
            >
              {somAtivo ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
            </Button>
            
            <Button
              variant="outline"
              size="icon"
              onClick={() => setShowSettings(!showSettings)}
              title="Configurações"
            >
              <Settings className="h-5 w-5" />
            </Button>
            
            <Button
              variant="outline"
              size="icon"
              onClick={toggleFullscreen}
              title={isFullscreen ? "Sair Fullscreen" : "Fullscreen"}
            >
              {isFullscreen ? <Minimize className="h-5 w-5" /> : <Maximize className="h-5 w-5" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Painel de Configurações */}
      {showSettings && (
        <Card className={`mb-6 ${modoNoturno ? 'bg-gray-800 border-gray-700' : ''}`}>
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-6">
              <div className="flex items-center gap-3">
                <Volume2 className={`h-5 w-5 ${modoNoturno ? 'text-gray-400' : 'text-gray-600'}`} />
                <span className={`text-sm font-medium ${modoNoturno ? 'text-gray-300' : ''}`}>Volume:</span>
                <Slider
                  value={[volume]}
                  onValueChange={(val) => setVolume(val[0])}
                  max={100}
                  step={10}
                  className="w-32"
                />
                <span className={`text-sm ${modoNoturno ? 'text-gray-400' : 'text-gray-500'}`}>{volume}%</span>
              </div>
              
              <div className="flex items-center gap-3">
                <span className={`text-sm font-medium ${modoNoturno ? 'text-gray-300' : ''}`}>Testar Sons:</span>
                {Object.entries(NIVEIS_CONFIG).map(([key, config]) => (
                  <Button
                    key={key}
                    size="sm"
                    variant="outline"
                    onClick={() => tocarSom(key, volume / 100)}
                    className={`${config.cor} text-white border-0 hover:opacity-80`}
                  >
                    {config.nome.split(' ')[0]}
                  </Button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Contadores por Nível */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {Object.entries(NIVEIS_CONFIG).map(([key, config]) => (
          <Card 
            key={key} 
            className={`${modoNoturno ? 'bg-gray-800 border-gray-700' : config.corFundo} border-l-4 ${config.corBorda}`}
          >
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className={`text-xs font-medium ${modoNoturno ? 'text-gray-400' : 'text-gray-500'}`}>
                  {config.nome}
                </p>
                <p className={`text-3xl font-bold ${modoNoturno ? 'text-white' : config.corTexto}`}>
                  {contadores[key] || 0}
                </p>
              </div>
              <div className={`w-12 h-12 rounded-full ${config.cor} flex items-center justify-center opacity-80`}>
                <Users className="w-6 h-6 text-white" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Alunos Chamados - Destaque */}
      {chamados.length > 0 && (
        <div className="mb-6">
          <h2 className={`text-xl font-bold mb-4 flex items-center gap-2 ${modoNoturno ? 'text-white' : 'text-gray-800'}`}>
            <Bell className="w-6 h-6 text-blue-500 animate-bounce" />
            ALUNOS CHAMADOS
            <Badge className="bg-blue-500 animate-pulse ml-2">{chamados.length}</Badge>
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {chamados.map((item) => {
              const nivel = identificarNivel(item.turma_nome);
              const config = NIVEIS_CONFIG[nivel];
              
              return (
                <Card 
                  key={item.aluno_id}
                  className={`relative overflow-hidden border-4 ${config.corBorda} ${modoNoturno ? 'bg-gray-800' : 'bg-white'} shadow-2xl animate-pulse`}
                >
                  <div className={`absolute top-0 left-0 right-0 h-2 ${config.cor}`} />
                  
                  <CardContent className="p-6 pt-8">
                    <div className="flex items-center gap-4">
                      {/* Foto grande */}
                      <div className={`w-24 h-24 rounded-2xl ${config.cor} flex items-center justify-center overflow-hidden shadow-lg flex-shrink-0`}>
                        {item.aluno_foto ? (
                          <img src={item.aluno_foto} alt="" className="w-24 h-24 object-cover" />
                        ) : (
                          <span className="text-4xl text-white font-bold">
                            {item.aluno_nome?.charAt(0).toUpperCase()}
                          </span>
                        )}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <h3 className={`text-xl font-bold truncate ${modoNoturno ? 'text-white' : 'text-gray-900'}`}>
                          {item.aluno_nome}
                        </h3>
                        <p className={`text-lg ${modoNoturno ? 'text-gray-300' : 'text-gray-600'}`}>
                          {item.turma_nome}
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          <Clock className={`w-4 h-4 ${config.corTexto}`} />
                          <span className={`text-sm font-medium ${config.corTexto}`}>
                            Chamado {formatarTempo(item.hora_chamada)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Fila de Aguardando */}
      <div>
        <h2 className={`text-lg font-bold mb-4 flex items-center gap-2 ${modoNoturno ? 'text-white' : 'text-gray-800'}`}>
          <Clock className="w-5 h-5 text-amber-500" />
          AGUARDANDO NA FILA
          <Badge variant="secondary" className="ml-2">{fila.length}</Badge>
        </h2>
        
        {fila.length === 0 ? (
          <Card className={`border-dashed ${modoNoturno ? 'bg-gray-800 border-gray-700' : ''}`}>
            <CardContent className="py-12 text-center">
              <Users className={`w-12 h-12 mx-auto mb-3 ${modoNoturno ? 'text-gray-600' : 'text-gray-300'}`} />
              <p className={`text-lg ${modoNoturno ? 'text-gray-400' : 'text-gray-500'}`}>
                Nenhum aluno aguardando na fila
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
            {fila.map((item) => {
              const nivel = identificarNivel(item.turma_nome);
              const config = NIVEIS_CONFIG[nivel];
              
              return (
                <Card 
                  key={item.aluno_id}
                  className={`border-l-4 ${config.corBorda} ${modoNoturno ? 'bg-gray-800' : config.corFundo}`}
                >
                  <CardContent className="p-3">
                    <div className="flex items-center gap-2">
                      <div className={`w-10 h-10 rounded-lg ${config.cor} flex items-center justify-center flex-shrink-0`}>
                        {item.aluno_foto ? (
                          <img src={item.aluno_foto} alt="" className="w-10 h-10 object-cover rounded-lg" />
                        ) : (
                          <span className="text-lg text-white font-bold">
                            {item.aluno_nome?.charAt(0).toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className={`text-sm font-medium truncate ${modoNoturno ? 'text-white' : 'text-gray-900'}`}>
                          {item.aluno_nome?.split(' ')[0]}
                        </p>
                        <p className={`text-xs truncate ${modoNoturno ? 'text-gray-400' : 'text-gray-500'}`}>
                          {item.turma_nome?.split(' - ')[0]}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Indicador de refresh */}
      <div className={`fixed bottom-4 right-4 flex items-center gap-2 text-sm ${modoNoturno ? 'text-gray-500' : 'text-gray-400'}`}>
        <RefreshCw className="w-4 h-4 animate-spin" />
        Auto-refresh: 2s
      </div>
      <DnaGoDnAiWidget />
    </div>
  );
}
